import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Save, CreditCard, Smartphone, Building2, Upload } from 'lucide-react';
import { useToast } from './Toast';

const PaymentSettingsForm = ({ 
  initialSettings = null, 
  onSave, 
  onCancel,
  isLoading = false,
  className = "" 
}) => {
  const [settings, setSettings] = useState({
    bank: {
      name: '',
      accountNo: '',
      ifsc: '',
      accountHolderName: ''
    },
    upi: '',
    qrCodeImage: '',
    instructions: ''
  });

  const [errors, setErrors] = useState({});
  const [uploadingQr, setUploadingQr] = useState(false);
  const { showSuccess, showError } = useToast();

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => reject(e);
    });

  const handleQrImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError('Please select an image file (PNG, JPG, etc.)');
      return;
    }
    setUploadingQr(true);
    try {
      const base64 = await fileToBase64(file);
      updateField('qrCodeImage', base64);
      showSuccess('QR code image uploaded!');
    } catch (err) {
      showError(err?.message || 'Upload failed');
    } finally {
      setUploadingQr(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (initialSettings) {
      // Handle both old flat structure and new nested structure
      const normalizedSettings = {
        bank: {
          name: initialSettings.bank?.name || initialSettings.bankName || '',
          accountNo: initialSettings.bank?.accountNo || initialSettings.accountNo || '',
          ifsc: initialSettings.bank?.ifsc || initialSettings.ifsc || '',
          accountHolderName: initialSettings.bank?.accountHolderName || initialSettings.accountHolderName || ''
        },
        upi: initialSettings.upi || '',
        qrCodeImage: initialSettings.qrCodeImage || '',
        instructions: initialSettings.instructions || ''
      };
      setSettings(normalizedSettings);
    }
  }, [initialSettings]);

  const updateBankField = (field, value) => {
    setSettings(prev => ({
      ...prev,
      bank: {
        ...prev.bank,
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors[`bank.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`bank.${field}`]: null
      }));
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Bank validation
    if (!settings.bank.name.trim()) {
      newErrors['bank.name'] = 'Bank name is required';
    }
    if (!settings.bank.accountNo.trim()) {
      newErrors['bank.accountNo'] = 'Account number is required';
    }
    if (!settings.bank.ifsc.trim()) {
      newErrors['bank.ifsc'] = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(settings.bank.ifsc.toUpperCase())) {
      newErrors['bank.ifsc'] = 'Invalid IFSC code format';
    }
    if (!settings.bank.accountHolderName.trim()) {
      newErrors['bank.accountHolderName'] = 'Account holder name is required';
    }

    // UPI validation
    if (!settings.upi.trim()) {
      newErrors['upi'] = 'UPI ID is required';
    } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/.test(settings.upi)) {
      newErrors['upi'] = 'Invalid UPI ID format';
    }

    // Instructions validation
    if (!settings.instructions.trim()) {
      newErrors['instructions'] = 'Payment instructions are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm() && onSave) {
      onSave(settings);
    }
  };

  const handleReset = () => {
    setSettings({
      bank: {
        name: '',
        accountNo: '',
        ifsc: '',
        accountHolderName: ''
      },
      upi: '',
      qrCodeImage: '',
      instructions: ''
    });
    setErrors({});
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Bank Account Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">Bank Account Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name *
            </label>
            <Input
              value={settings.bank.name}
              onChange={(e) => updateBankField('name', e.target.value)}
              placeholder="e.g., HDFC Bank, SBI, ICICI Bank"
              className={errors['bank.name'] ? 'border-red-500' : ''}
            />
            {errors['bank.name'] && (
              <p className="text-red-500 text-sm mt-1">{errors['bank.name']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number *
            </label>
            <Input
              value={settings.bank.accountNo}
              onChange={(e) => updateBankField('accountNo', e.target.value)}
              placeholder="Enter account number"
              className={errors['bank.accountNo'] ? 'border-red-500' : ''}
            />
            {errors['bank.accountNo'] && (
              <p className="text-red-500 text-sm mt-1">{errors['bank.accountNo']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              IFSC Code *
            </label>
            <Input
              value={settings.bank.ifsc}
              onChange={(e) => updateBankField('ifsc', e.target.value.toUpperCase())}
              placeholder="e.g., HDFC0001234"
              className={errors['bank.ifsc'] ? 'border-red-500' : ''}
              maxLength={11}
            />
            {errors['bank.ifsc'] && (
              <p className="text-red-500 text-sm mt-1">{errors['bank.ifsc']}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Format: 4 letters + 0 + 6 alphanumeric characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Holder Name *
            </label>
            <Input
              value={settings.bank.accountHolderName}
              onChange={(e) => updateBankField('accountHolderName', e.target.value)}
              placeholder="Enter account holder name"
              className={errors['bank.accountHolderName'] ? 'border-red-500' : ''}
            />
            {errors['bank.accountHolderName'] && (
              <p className="text-red-500 text-sm mt-1">{errors['bank.accountHolderName']}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* UPI Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            <CardTitle className="text-lg">UPI Payment Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              UPI ID *
            </label>
            <Input
              value={settings.upi}
              onChange={(e) => updateField('upi', e.target.value)}
              placeholder="e.g., eventpayments@paytm, yourname@upi"
              className={errors['upi'] ? 'border-red-500' : ''}
            />
            {errors['upi'] && (
              <p className="text-red-500 text-sm mt-1">{errors['upi']}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Users can pay using any UPI app (Google Pay, PhonePe, Paytm, etc.)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              QR Code Image (Optional)
            </label>
            <div className="flex gap-2">
              <Input
                value={settings.qrCodeImage}
                onChange={(e) => updateField('qrCodeImage', e.target.value)}
                placeholder="Paste QR code image URL or base64, or upload"
                className={`flex-1 ${errors['qrCodeImage'] ? 'border-red-500' : ''}`}
              />
              <label className="flex items-center px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm shrink-0">
                <Upload className="w-4 h-4 mr-1" />
                {uploadingQr ? 'Uploading...' : 'Upload'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleQrImageUpload}
                  disabled={uploadingQr}
                />
              </label>
            </div>
            {errors['qrCodeImage'] && (
              <p className="text-red-500 text-sm mt-1">{errors['qrCodeImage']}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Optional: Add a QR code image for easier payments. Paste URL/base64 or upload an image.
            </p>
            {settings.qrCodeImage && (
              <div className="mt-2">
                <img 
                  src={settings.qrCodeImage} 
                  alt="QR Code Preview" 
                  className="w-32 h-32 object-contain border rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment Instructions */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-purple-600" />
            <CardTitle className="text-lg">Payment Instructions</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instructions for Users *
            </label>
            <Textarea
              value={settings.instructions}
              onChange={(e) => updateField('instructions', e.target.value)}
              placeholder="e.g., Please include your email address in the payment notes/reference. After payment, share the screenshot via email or WhatsApp."
              rows={4}
              className={errors['instructions'] ? 'border-red-500' : ''}
            />
            {errors['instructions'] && (
              <p className="text-red-500 text-sm mt-1">{errors['instructions']}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              These instructions will be shown to users during registration for paid events
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="border-dashed border-2 border-gray-300">
        <CardHeader>
          <CardTitle className="text-lg text-gray-600">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-900">Bank Transfer:</h4>
              <div className="bg-gray-50 p-3 rounded-md mt-1">
                <p><strong>Bank:</strong> {settings.bank.name || 'Bank Name'}</p>
                <p><strong>Account No:</strong> {settings.bank.accountNo || 'Account Number'}</p>
                <p><strong>IFSC:</strong> {settings.bank.ifsc || 'IFSC Code'}</p>
                <p><strong>Account Holder:</strong> {settings.bank.accountHolderName || 'Account Holder Name'}</p>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900">UPI Payment:</h4>
              <div className="bg-gray-50 p-3 rounded-md mt-1">
                <p><strong>UPI ID:</strong> {settings.upi || 'your-upi-id@upi'}</p>
                {settings.qrCodeImage && (
                  <div className="mt-2">
                    <p><strong>QR Code:</strong></p>
                    <img 
                      src={settings.qrCodeImage} 
                      alt="QR Code" 
                      className="w-20 h-20 object-contain border rounded mt-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900">Instructions:</h4>
              <div className="bg-gray-50 p-3 rounded-md mt-1">
                <p>{settings.instructions || 'Payment instructions will appear here'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <Button variant="outline" onClick={handleReset}>
          Reset
        </Button>
        <Button onClick={handleSave} disabled={isLoading}>
          <Save className="w-4 h-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default PaymentSettingsForm;
